import type { Types as CornerstoneTypes } from '@cornerstonejs/core';

/**
 * Drop any pending engine render so a following `resize()` does not bail.
 *
 * `renderingEngine` is Cornerstone's RenderingEngine *wrapper*, which only
 * delegates a fixed method set to its inner `_implementation`
 * (ContextPoolRenderingEngine). `_clearAnimationFrame` lives on that
 * implementation (via BaseRenderingEngine), so we reach through `_implementation`.
 *
 * Why this matters: `ContextPoolRenderingEngine._resizeVTKViewports` bails out
 * (`if (this._animationFrameSet) return`) without resizing the stack canvas
 * buffers whenever an engine render is already scheduled. A playing
 * `VideoViewport` fires IMAGE_RENDERED every frame, keeping a render perpetually
 * pending — so stack viewports sharing the engine never resize and their image
 * gets bitmap-stretched (broken aspect ratio). Clearing the pending frame first
 * makes the subsequent resize always apply; `resize(immediate=true)` re-schedules
 * the render afterwards, so nothing is lost.
 *
 * Guarded so a future Cornerstone change degrades gracefully instead of throwing.
 */
export function clearEnginePendingRender(
  renderingEngine: CornerstoneTypes.IRenderingEngine
): void {
  const wrapper = renderingEngine as unknown as {
    _implementation?: { _clearAnimationFrame?: () => void };
    _clearAnimationFrame?: () => void;
  };
  const impl = wrapper._implementation ?? wrapper;
  if (typeof impl._clearAnimationFrame === 'function') {
    impl._clearAnimationFrame();
  }
}

interface ResizeViewportsOptions {
  /** Restrict the refit to a single viewport (single-viewport callers). */
  viewportId?: string;
}

type Viewport = CornerstoneTypes.IViewport;
type Point3 = CornerstoneTypes.Point3;

/** The user's camera, expressed relative to fit-to-cell so it survives a refit. */
interface CameraSnapshot {
  /** Magnification relative to the fit-to-cell scale (1 = fit). */
  zoom: number;
  /** World point at the center of the view. */
  focalPoint: Point3;
}

/** VideoViewport internals we read; not part of Cornerstone's public types. */
interface VideoViewportInternals {
  videoWidth: number;
  videoHeight: number;
  videoCamera: { parallelScale: number };
  canvasToWorld: (canvasPos: [number, number]) => Point3;
}

function isVideoViewport(vp: Viewport): boolean {
  return typeof (vp as CornerstoneTypes.IVideoViewport).play === 'function';
}

/**
 * Must run BEFORE `renderingEngine.resize()`, while the canvas still describes
 * the old cell. Stack zoom is `getZoom()` (vs. the fit camera stored by the last
 * resetCamera, in world units). Video has no fit camera, so its fit ratio is
 * recomputed from the old canvas buffer (`VideoViewport.resize` sets it to the
 * CSS size) the same way `refreshRenderValues` does.
 */
function captureCamera(vp: Viewport): CameraSnapshot | null {
  if (!isVideoViewport(vp)) {
    const { focalPoint } = vp.getCamera();
    if (!focalPoint || focalPoint.some(Number.isNaN)) return null;
    return { zoom: vp.getZoom(), focalPoint };
  }
  const video = vp as unknown as VideoViewportInternals;
  const { width, height } = vp.getCanvas();
  if (!width || !height || !video.videoWidth || !video.videoHeight) return null;
  const fitRatio = Math.min(
    width / video.videoWidth,
    height / video.videoHeight
  );
  return {
    zoom: video.videoCamera.parallelScale / fitRatio,
    focalPoint: video.canvasToWorld([width / 2, height / 2]),
  };
}

/**
 * Re-apply a snapshot on top of a fresh fit. `setCamera` is called without
 * `storeAsInitialCamera`, so Reset still returns to fit-to-cell.
 */
function restoreCamera(vp: Viewport, snapshot: CameraSnapshot): void {
  const { zoom, focalPoint } = snapshot;
  if (isVideoViewport(vp)) {
    const fitRatio = (vp as unknown as VideoViewportInternals).videoCamera
      .parallelScale;
    // VideoViewport.setCamera takes parallelScale as world half-height.
    vp.setCamera({
      parallelScale: vp.element.clientHeight / 2 / (fitRatio * zoom),
      focalPoint,
    });
    return;
  }
  const fit = vp.getCamera();
  // Keep the refit camera's distance along the view normal.
  const position = focalPoint.map(
    (v, i) => v + fit.position![i] - fit.focalPoint![i]
  ) as Point3;
  vp.setCamera({
    ...fit,
    parallelScale: fit.parallelScale! / zoom,
    focalPoint,
    position,
  });
}

/**
 * Resize every viewport in the shared engine, refit the image to its new box and
 * re-apply each pane's own pan/zoom.
 *
 * Used by BOTH resize triggers so they behave identically:
 *  - the grid panel-drag `ResizeObserver` (useViewportResize)
 *  - the grid layout-change effect (ViewerProvider, e.g. 1x4 -> 2x2)
 *
 * Steps:
 * 1. Snapshot each viewport's camera relative to fit (see `captureCamera`).
 * 2. `clearEnginePendingRender` so `resize()` is not dropped (see above).
 * 3. `renderingEngine.resize()` — updates the stack canvas drawing buffers.
 * 4. `vp.resetCamera()` per viewport, then restore the snapshot. We can't rely
 *    on `resize()`'s `keepCamera=true`: it restores the old *world* camera, so
 *    the image keeps its old scale and appears "stuck at the previous size" in
 *    the resized cell (and VideoViewport.resize() drops the camera entirely).
 *    Refitting first and re-applying zoom relative to fit keeps the user's
 *    magnification and centered point while adapting to the new cell. Only the
 *    explicit Reset clears pan/zoom.
 * 5. For video viewports, `customRenderViewportToCanvas()` forces a synchronous
 *    redraw; `VideoViewport.resize()` skips `renderFrame()` while playing, which
 *    would otherwise leave the canvas black until the next rAF.
 */
export function resizeAndRefitViewports(
  renderingEngine: CornerstoneTypes.IRenderingEngine,
  options: ResizeViewportsOptions = {}
): void {
  const viewports = (
    options.viewportId
      ? [renderingEngine.getViewport(options.viewportId)].filter(Boolean)
      : renderingEngine.getViewports()
  ).filter((vp) => vp.getCanvas());

  const snapshots = new Map<Viewport, CameraSnapshot>();
  viewports.forEach((vp) => {
    try {
      const snapshot = captureCamera(vp);
      if (snapshot) snapshots.set(vp, snapshot);
    } catch {
      // No camera yet (e.g. image still loading) — plain refit.
    }
  });

  clearEnginePendingRender(renderingEngine);

  renderingEngine.resize();

  viewports.forEach((vp) => {
    vp.resetCamera();

    const snapshot = snapshots.get(vp);
    if (snapshot) {
      try {
        restoreCamera(vp, snapshot);
      } catch {
        // Leave the pane at fit rather than in a half-applied camera.
        vp.resetCamera();
      }
    }

    if (isVideoViewport(vp)) {
      try {
        vp.customRenderViewportToCanvas();
      } catch {
        // Viewport may not support synchronous rendering.
      }
    }
  });
}
